import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCompanyLinkInPanel } from "@/hooks/useCompanyLinkInPanel";
import { useTranslation } from "@/hooks/useTranslation";
import { ConversationDTO } from "@shaarp/common";
import { RxExternalLink } from "react-icons/rx";
import { NavLink } from "react-router-dom";
import AvatarCompany from "../company/avatar-company";
import { internalLinkTarget } from "@/lib/internal-link-target";

export function AvatarChatCompaniesEnriched({
  conversation,
  tooltipContentSide,
  dropdownMenuContentAlign,
}: {
  conversation: ConversationDTO;
  tooltipContentSide?: "top" | "right" | "bottom" | "left";
  dropdownMenuContentAlign?: "start" | "center" | "end";
}) {
  const { t } = useTranslation();
  const openInPanel = useCompanyLinkInPanel();
  // A conversation about a company that is also its target carries the same one
  // twice, and both lists below key on its id. React then warns about two
  // children sharing a key and stops telling them apart, so the avatars remount
  // against each other — and two identical avatars stacked on top of each other
  // were never wanted anyway.
  //
  // Two entries, so the duplicate is one comparison rather than a scan. The
  // single-parameter `filter` is what makes the result non-nullable, which is
  // why nothing below has to ask again.
  const isTargetSameCompany =
    !!conversation.company &&
    conversation.company.id === conversation.companyTargeted?.id;
  const companiesEnriched = [
    conversation.company,
    isTargetSameCompany ? null : conversation.companyTargeted,
  ].filter((company) => !!company);

  return (
    <>
      {companiesEnriched.length > 0 && (
        <Tooltip>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <div className="flex flex-row">
                  {companiesEnriched.map((company, index) => (
                    <AvatarCompany
                      key={company.id}
                      className={`w-6 h-6 border bg-muted ${index > 0 && "-ml-3"}`}
                      company={company}
                    />
                  ))}
                </div>
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={dropdownMenuContentAlign}>
              {companiesEnriched.map((company) => (
                <DropdownMenuItem key={company.id} asChild>
                  <NavLink
                    to={`/company/${company.id}`}
                    target={internalLinkTarget()}
                    className="flex items-center gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      openInPanel(e, { id: company.id, label: company.name });
                    }}
                  >
                    <RxExternalLink className="w-4 h-4" />
                    {t("chat.conversation.companiesEnriched.viewCompany", {
                      companyName: company.name,
                    })}
                  </NavLink>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <TooltipContent side={tooltipContentSide}>
            {t("chat.conversation.companiesEnriched.tooltip", {
              companiesNames: companiesEnriched
                .map((company) => company.name)
                .join(", "),
            })}
          </TooltipContent>
        </Tooltip>
      )}
    </>
  );
}
